<template>
  <div class="ss-page">
    <div class="ss-container">
      <!-- Header -->
      <div class="ss-header">
        <div>
          <p class="ss-kicker">Site configuration</p>
          <h1 class="ss-title">Site Settings</h1>
          <p class="ss-subtitle" v-if="settings?.site">{{ settings.site.name }}</p>
        </div>
        <div class="ss-header-actions">
          <router-link
            :to="{ name: 'editor', params: { id: $route.params.id } }"
            class="ss-btn ss-btn-ghost"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Editor
          </router-link>
          <button
            @click="refreshSettings"
            :disabled="loading"
            class="ss-btn ss-btn-primary"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading && !settings" class="ss-loading">
        <div class="ss-spinner"></div>
        <p>Loading site settings...</p>
      </div>

      <!-- Settings Grid -->
      <div v-else-if="settings" class="ss-grid">
        <!-- Site Overview dashboard: traffic, GA4, submissions, history.
             SEO/AEO/GEO + Analytics config live in the cards further down. -->
        <div class="ss-overview-band">
          <SiteOverview :site-id="$route.params.id" />
        </div>

        <!-- Domain Configuration -->
        <div class="ss-card">
          <div class="ss-card-head">
            <h2 class="ss-card-title">Domain Configuration</h2>
            <div class="ss-status">
              <div class="ss-dot" :class="domainDotClass()"></div>
              <span class="ss-status-text">{{ getDomainStatusText() }}</span>
            </div>
          </div>

          <div class="ss-stack">
            <!-- Existing domains list -->
            <div v-if="settings.domain_status?.domains?.length" class="ss-domain-list">
              <div
                v-for="d in settings.domain_status.domains"
                :key="d.name"
                class="ss-domain-row"
              >
                <div class="ss-domain-row-info">
                  <span class="ss-domain-name">{{ d.name }}</span>
                  <span class="ss-pill" :class="d.status === 'active' ? 'ss-pill-success' : 'ss-pill-warning'">
                    {{ d.status === 'active' ? 'Active' : 'Pending' }}
                  </span>
                  <span class="ss-pill ss-pill-info" v-if="d.ssl_status === 'active'">SSL ✓</span>
                </div>
                <div v-if="canManageDomain" class="ss-domain-row-actions">
                  <template v-if="domainConfirmRemove === d.name">
                    <span class="ss-domain-confirm-text">Remove?</span>
                    <button class="ss-domain-remove ss-domain-remove-confirm" :disabled="domainRemoving.has(d.name)" @click="removeDomain(d.name)">Yes</button>
                    <button class="ss-btn ss-btn-ghost ss-btn-sm" @click="cancelRemoveDomain">Cancel</button>
                  </template>
                  <button
                    v-else
                    class="ss-domain-remove"
                    :disabled="domainRemoving.has(d.name)"
                    @click="confirmRemoveDomain(d.name)"
                    title="Remove domain"
                  >{{ domainRemoving.has(d.name) ? '…' : '✕' }}</button>
                </div>
              </div>
            </div>

            <!-- Fallback / pages.dev domain -->
            <div class="ss-subcard">
              <div class="ss-subcard-head">
                <h3 class="ss-subcard-title">
                  {{ settings.domain_status?.hasCustomDomain ? 'Fallback Domain' : 'Default Domain' }}
                </h3>
                <span class="ss-pill" :class="settings.domain_status?.hasCustomDomain ? 'ss-pill-info' : 'ss-pill-success'">
                  {{ settings.domain_status?.hasCustomDomain ? 'Backup' : 'Active' }}
                </span>
              </div>
              <p class="ss-domain-secondary">{{ settings.domain_status?.fallbackDomain }}</p>
            </div>

            <!-- Add domain form -->
            <div v-if="canManageDomain" class="ss-add-domain">
              <div v-if="!showDomainForm" class="ss-link-row">
                <button class="ss-link" @click="showDomainForm = true">+ Add Custom Domain</button>
              </div>
              <div v-else class="ss-domain-form">
                <input
                  v-model="domainInput"
                  class="ss-domain-input"
                  type="text"
                  placeholder="e.g. www.yoursite.com"
                  :disabled="domainAdding"
                  @keyup.enter="addDomain"
                  @keyup.escape="showDomainForm = false; domainInput = ''; domainError = ''"
                />
                <div class="ss-domain-form-actions">
                  <button class="ss-btn ss-btn-primary ss-btn-sm" :disabled="domainAdding || !domainInput.trim()" @click="addDomain">
                    {{ domainAdding ? 'Adding…' : 'Add Domain' }}
                  </button>
                  <button class="ss-btn ss-btn-ghost ss-btn-sm" :disabled="domainAdding" @click="showDomainForm = false; domainInput = ''; domainError = ''">
                    Cancel
                  </button>
                </div>
                <p v-if="domainError" class="ss-domain-error">{{ domainError }}</p>
              </div>
            </div>

            <!-- DNS instructions shown for pending domains -->
            <div v-if="settings.domain_status?.domains?.some(d => d.status !== 'active')" class="ss-dns-instructions">
              <h4 class="ss-dns-title">DNS Setup Required</h4>
              <p class="ss-dns-desc">
                Point your domain to Cloudflare Pages by adding a CNAME record in your DNS provider:
              </p>
              <div v-for="pd in settings.domain_status.domains.filter(d => d.status !== 'active')" :key="pd.name" class="ss-dns-row">
                <span class="ss-dns-domain">{{ pd.name }}</span>
                <div class="ss-dns-record">
                  <span class="ss-dns-label">CNAME</span>
                  <span class="ss-dns-value">{{ settings.domain_status.dnsTarget || settings.domain_status.fallbackDomain?.replace('https://', '') }}</span>
                  <button class="ss-dns-copy" @click="copyDnsValue(settings.domain_status.dnsTarget || settings.domain_status.fallbackDomain?.replace('https://', ''))" title="Copy">⎘</button>
                </div>
              </div>
              <p class="ss-dns-note">DNS propagation can take up to 24 hours. Refresh this page to check status.</p>
            </div>

            <!-- Success banner -->
            <div v-if="domainSuccess" class="ss-domain-success">{{ domainSuccess }}</div>
          </div>
        </div>

        <!-- Deployment Status -->
        <div class="ss-card">
          <div class="ss-card-head">
            <h2 class="ss-card-title">Deployment Status</h2>
            <div class="ss-status">
              <div class="ss-dot" :class="deployHeaderDotClass"></div>
              <span class="ss-status-text">{{ deployHeaderText }}</span>
            </div>
          </div>

          <div class="ss-stack" v-if="settings.deployment_info?.cloudflare_enabled">
            <!-- What's LIVE now -->
            <div class="ss-subcard ss-deploy-live">
              <div class="ss-subcard-head">
                <h3 class="ss-subcard-title">Live on production</h3>
                <span v-if="settings.deployment_info.live" :class="['ss-pill', deployPillClass(settings.deployment_info.live.state)]">
                  {{ deployStateText(settings.deployment_info.live.state) }}
                </span>
                <span v-else class="ss-pill ss-pill-neutral">Nothing live yet</span>
              </div>
              <div v-if="settings.deployment_info.live" class="ss-deploy-meta">
                <div><span class="ss-meta-label">Serving commit:</span>
                  <span class="ss-chip-mono">{{ settings.deployment_info.live.commit_hash || '—' }}</span>
                  <span class="ss-chip-mono">{{ settings.deployment_info.live.branch }}</span>
                </div>
                <div><span class="ss-meta-label">Since:</span> {{ formatDeploymentTime(settings.deployment_info.live.created_on) }}</div>
                <div v-if="settings.deployment_info.live.commit_message" class="ss-deploy-msg">{{ settings.deployment_info.live.commit_message }}</div>
              </div>
              <div v-else class="ss-empty-inline">No successful production deployment yet.</div>
            </div>

            <!-- In-progress / failed newer build, only when there is one -->
            <div v-if="settings.deployment_info.pending" class="ss-subcard" :class="settings.deployment_info.pending.state === 'failed' ? 'ss-deploy-failed' : 'ss-deploy-building'">
              <div class="ss-subcard-head">
                <h3 class="ss-subcard-title">
                  {{ settings.deployment_info.pending.state === 'failed' ? 'Last deploy failed' : 'Deploying now' }}
                </h3>
                <span :class="['ss-pill', deployPillClass(settings.deployment_info.pending.state)]">
                  {{ deployStateText(settings.deployment_info.pending.state, settings.deployment_info.pending.stage) }}
                </span>
              </div>
              <div class="ss-deploy-meta">
                <div><span class="ss-meta-label">Commit:</span>
                  <span class="ss-chip-mono">{{ settings.deployment_info.pending.commit_hash || '—' }}</span>
                  <span class="ss-chip-mono">{{ settings.deployment_info.pending.branch }}</span>
                </div>
                <div><span class="ss-meta-label">{{ settings.deployment_info.pending.state === 'failed' ? 'Failed:' : 'Started:' }}</span>
                  {{ formatDeploymentTime(settings.deployment_info.pending.created_on) }}</div>
                <div v-if="settings.deployment_info.pending.state === 'failed'" class="ss-deploy-note">
                  Production is still serving the last good build above. Push a fix or retry the deploy.
                </div>
              </div>
            </div>

            <!-- Recent history -->
            <div class="ss-subcard" v-if="settings.deployment_info.recent?.length">
              <h3 class="ss-subcard-title">Recent deployments</h3>
              <div class="ss-deploy-history">
                <div v-for="d in settings.deployment_info.recent" :key="d.id" class="ss-deploy-row">
                  <span :class="['ss-dot', deployDotClass(d.state)]"></span>
                  <span class="ss-deploy-env">{{ d.environment === 'production' ? 'prod' : 'preview' }}</span>
                  <span class="ss-chip-mono">{{ d.branch }}</span>
                  <span class="ss-chip-mono ss-deploy-commit">{{ d.commit_hash || '—' }}</span>
                  <span :class="['ss-pill', 'ss-pill-sm', deployPillClass(d.state)]">{{ deployStateText(d.state) }}</span>
                  <span class="ss-deploy-time">{{ formatDeploymentTime(d.created_on) }}</span>
                </div>
              </div>
            </div>
            <p v-if="settings.deployment_info.checked_at" class="ss-deploy-checked">
              Checked {{ formatDeploymentTime(settings.deployment_info.checked_at) }}
            </p>
          </div>

          <div v-else class="ss-empty-block">
            <svg class="ss-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>Cloudflare Pages not connected</p>
          </div>
        </div>

        <!-- Search Engine Status -->
        <div class="ss-card">
          <div class="ss-card-head">
            <h2 class="ss-card-title">Search Engine Integration</h2>
            <router-link
              :to="{ name: 'editor', params: { id: $route.params.id }, query: { modal: 'seo' } }"
              class="ss-link"
            >
              Submit to Search Engines
            </router-link>
          </div>

          <div class="ss-stack">
            <!-- IndexNow -->
            <div class="ss-integration">
              <div class="ss-integration-left">
                <div class="ss-integration-icon" style="background:#a855f7">
                  <span>I</span>
                </div>
                <div>
                  <p class="ss-integration-name">IndexNow Protocol</p>
                  <p class="ss-integration-desc">Bing, Yandex, Seznam.cz, Naver</p>
                </div>
              </div>
              <span class="ss-pill ss-pill-success">✓ Active</span>
            </div>

            <!-- Google Search Console -->
            <div class="ss-integration">
              <div class="ss-integration-left">
                <div class="ss-integration-icon" style="background:#3b82f6">
                  <span>G</span>
                </div>
                <div>
                  <p class="ss-integration-name">Google Search Console</p>
                  <p class="ss-integration-desc">Sitemap submission, URL inspection, real-time indexing</p>
                </div>
              </div>
              <span class="ss-pill ss-pill-success">✓ Active</span>
            </div>

            <!-- Bing Webmaster Tools -->
            <div class="ss-integration">
              <div class="ss-integration-left">
                <div class="ss-integration-icon" style="background:#16a34a">
                  <span>B</span>
                </div>
                <div>
                  <p class="ss-integration-name">Bing Webmaster Tools</p>
                  <p class="ss-integration-desc">Direct URL submission, sitemap management, crawl stats</p>
                </div>
              </div>
              <span class="ss-pill ss-pill-success">✓ Available</span>
            </div>
          </div>
        </div>

        <!-- SEO Performance — data-driven from /api/bwai/sites/:id/seo/performance -->
        <div class="ss-card" data-section="seo-performance">
          <div class="ss-card-head">
            <div>
              <h2 class="ss-card-title">SEO Performance</h2>
              <span v-if="seoLastRefreshedLabel && !seoRefreshing" class="ss-seo-last-refresh">
                Last analyzed {{ seoLastRefreshedLabel }}<span v-if="seoPerformance?.commit_sha" class="ss-seo-commit"> · {{ seoPerformance.commit_sha }}</span>
              </span>
            </div>
            <button
              class="ss-seo-refresh-btn"
              :disabled="seoRefreshing || seoLoading"
              @click="triggerSeoRefresh"
            >
              <svg class="ss-seo-refresh-icon" :class="{ 'ss-spin': seoRefreshing }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {{ seoRefreshing ? 'Refreshing…' : 'Refresh' }}
            </button>
          </div>
          <div v-if="seoRefreshing || seoRefreshDone" class="ss-seo-progress-wrap">
            <div class="ss-seo-progress-bar" :style="{ width: seoRefreshProgress + '%' }" :class="{ 'ss-seo-progress-fail': seoRefreshFailed }"></div>
          </div>

          <div v-if="seoLoading" class="ss-meta-muted" data-seo-state="loading">Loading SEO data…</div>
          <div v-else-if="seoError" class="ss-domain-error" data-seo-state="error">
            {{ seoError }}
            <button
              class="ss-seo-retry-btn"
              :disabled="seoLoading"
              data-testid="seo-performance-retry"
              @click="fetchSeoPerformance"
            >Retry</button>
          </div>
          <!-- AC006 (local-analyzer story): empty-state copy is
               integration-agnostic. It must explain that local technical
               analysis is unavailable, and must NOT instruct the user to
               connect Google Search Console, PageSpeed, or any other
               external SEO provider. -->
          <div
            v-else-if="!seoPerformance || !seoHasData"
            class="ss-empty-block"
            data-seo-state="empty"
            :data-seo-empty-reason="!seoPerformance ? 'no_response' : 'metrics_empty'"
          >
            <svg class="ss-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>Technical SEO analysis is unavailable</p>
            <p class="ss-empty-detail">
              We couldn't reach your site's pages to compute a Technical SEO Score.
              Verify the site has a published production domain and try again.
            </p>
          </div>
          <div v-else class="ss-stack" data-seo-state="ready">
            <p v-if="seoPerformance.note" class="ss-meta-muted ss-seo-note">{{ seoPerformance.note }}</p>

            <!-- Horizontal SEO metric cards -->
            <div class="ss-seo-cards">

              <!-- Technical SEO Score — full card with breakdown -->
              <div class="ss-seo-hcard" :class="seoScoreClass(seoTechnicalScore)" data-seo-metric="technical-score">
                <div class="ss-seo-hcard-left">
                  <span class="ss-seo-hcard-value">{{ seoTechnicalScore ?? '--' }}</span>
                  <span class="ss-seo-hcard-sub">/100</span>
                </div>
                <div class="ss-seo-hcard-right">
                  <span class="ss-seo-hcard-label">Technical SEO Score</span>
                  <div v-if="seoPerformance.technical_seo?.score_breakdown" class="ss-seo-breakdown">
                    <div v-for="(crit, key) in seoBreakdownRows" :key="key" class="ss-seo-breakdown-row">
                      <span class="ss-seo-breakdown-name">{{ crit.label }}</span>
                      <div class="ss-seo-breakdown-bar-wrap">
                        <div class="ss-seo-breakdown-bar" :style="{ width: ((crit.earned / crit.max) * 100) + '%' }" :class="crit.earned === crit.max ? 'ss-seo-bar-full' : crit.earned > 0 ? 'ss-seo-bar-partial' : 'ss-seo-bar-zero'"></div>
                      </div>
                      <span class="ss-seo-breakdown-pts">{{ crit.earned }}/{{ crit.max }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AC009 (AEO/GEO story): the SEO performance section
                   surfaces all three canonical in-house scores (Technical
                   SEO, AEO, GEO) with per-check breakdowns + remediation
                   guidance. Test coverage:
                   site-settings-aeo-geo-scorecards.test.mjs. -->
              <!-- AC009 (AEO/GEO story): AEO Score card with explainable
                   breakdown. The score is the canonical in-house value;
                   failed checks list reason codes the user can act on. -->
              <div class="ss-seo-hcard" :class="seoScoreClass(aeoScore)" data-seo-metric="aeo-score">
                <div class="ss-seo-hcard-left">
                  <span class="ss-seo-hcard-value">{{ aeoScore ?? '--' }}</span>
                  <span class="ss-seo-hcard-sub">/100</span>
                </div>
                <div class="ss-seo-hcard-right">
                  <span class="ss-seo-hcard-label">AEO Score</span>
                  <span class="ss-seo-hcard-detail">Answer Engine Optimization — how well your pages are formatted for FAQ, voice, and chat answer engines.</span>
                  <div v-if="aeoBreakdownRows.length" class="ss-seo-breakdown" data-seo-breakdown="aeo">
                    <div v-for="row in aeoBreakdownRows" :key="row.key" class="ss-seo-breakdown-row" :data-seo-check="row.key" :data-seo-check-passed="row.passed">
                      <span class="ss-seo-breakdown-name">{{ row.label }}</span>
                      <div class="ss-seo-breakdown-bar-wrap">
                        <div class="ss-seo-breakdown-bar" :style="{ width: ((row.value / row.weight) * 100) + '%' }" :class="row.passed === true ? 'ss-seo-bar-full' : row.passed === 'partial' ? 'ss-seo-bar-partial' : 'ss-seo-bar-zero'"></div>
                      </div>
                      <span class="ss-seo-breakdown-pts">{{ row.value }}/{{ row.weight }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AC009 (AEO/GEO story): GEO Score card. Same breakdown
                   pattern. Combines site-level signals (llms.txt, robots
                   AI-bot allowlist) with per-page LLM-citation readiness. -->
              <div class="ss-seo-hcard" :class="seoScoreClass(geoScore)" data-seo-metric="geo-score">
                <div class="ss-seo-hcard-left">
                  <span class="ss-seo-hcard-value">{{ geoScore ?? '--' }}</span>
                  <span class="ss-seo-hcard-sub">/100</span>
                </div>
                <div class="ss-seo-hcard-right">
                  <span class="ss-seo-hcard-label">GEO Score</span>
                  <span class="ss-seo-hcard-detail">Generative Engine Optimization — how reachable, fresh, and citation-ready your pages are for LLM answer engines.</span>
                  <div v-if="geoBreakdownRows.length" class="ss-seo-breakdown" data-seo-breakdown="geo">
                    <div v-for="row in geoBreakdownRows" :key="row.key" class="ss-seo-breakdown-row" :data-seo-check="row.key" :data-seo-check-passed="row.passed">
                      <span class="ss-seo-breakdown-name">{{ row.label }}</span>
                      <div class="ss-seo-breakdown-bar-wrap">
                        <div class="ss-seo-breakdown-bar" :style="{ width: ((row.value / row.weight) * 100) + '%' }" :class="row.passed === true ? 'ss-seo-bar-full' : row.passed === 'partial' ? 'ss-seo-bar-partial' : 'ss-seo-bar-zero'"></div>
                      </div>
                      <span class="ss-seo-breakdown-pts">{{ row.value }}/{{ row.weight }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AC010 (AEO/GEO story): calibration badge sits in its own
                   diagnostic layer AFTER the canonical AEO + GEO score
                   cards (visual primacy via document order). Four labeled
                   states — disabled / unavailable / failed / ok — keep the
                   benchmark-vs-in-house distinction explicit. Coverage in
                   site-settings-aeo-geo-scorecards.test.mjs (AC010
                   four-state branch test). -->
              <div class="ss-seo-calibration" data-seo-calibration :data-seo-calibration-status="calibrationStatus.status" v-if="calibrationStatus">
                <span class="ss-seo-calibration-label">External benchmark:</span>
                <span class="ss-seo-calibration-state">
                  <template v-if="calibrationStatus.status === 'disabled'">Disabled — in-house scoring only</template>
                  <template v-else-if="calibrationStatus.status === 'unavailable'">Unavailable — {{ calibrationStatus.error || 'no benchmark provider configured' }}</template>
                  <template v-else-if="calibrationStatus.status === 'failed'">Failed — {{ calibrationStatus.error || 'benchmark fetch errored' }}</template>
                  <template v-else-if="calibrationStatus.status === 'ok'">
                    <span class="ss-seo-calibration-delta" :class="calibrationStatus.aeo_delta != null && calibrationStatus.aeo_delta >= 0 ? 'ss-seo-calibration-ahead' : 'ss-seo-calibration-behind'">AEO {{ calibrationStatus.aeo_delta != null ? (calibrationStatus.aeo_delta >= 0 ? '+' : '') + calibrationStatus.aeo_delta : '—' }}</span>
                    <span class="ss-seo-calibration-delta" :class="calibrationStatus.geo_delta != null && calibrationStatus.geo_delta >= 0 ? 'ss-seo-calibration-ahead' : 'ss-seo-calibration-behind'">GEO {{ calibrationStatus.geo_delta != null ? (calibrationStatus.geo_delta >= 0 ? '+' : '') + calibrationStatus.geo_delta : '—' }}</span>
                    <span class="ss-seo-calibration-source" v-if="calibrationStatus.source">via {{ calibrationStatus.source }}</span>
                  </template>
                </span>
              </div>

              <!-- Mobile Friendliness -->
              <div class="ss-seo-hcard" data-seo-metric="mobile-friendliness">
                <div class="ss-seo-hcard-left">
                  <span class="ss-seo-hcard-value ss-seo-hcard-value-sm">{{ seoPerformance.technical_seo?.mobile_friendliness === 'Good' ? '✓' : seoPerformance.technical_seo?.mobile_friendliness ? '!' : '--' }}</span>
                </div>
                <div class="ss-seo-hcard-right">
                  <span class="ss-seo-hcard-label">Mobile Friendliness</span>
                  <span class="ss-seo-hcard-detail">{{ seoPerformance.technical_seo?.mobile_friendliness ?? 'Unknown' }}</span>
                </div>
              </div>

              <!-- HTTPS -->
              <div class="ss-seo-hcard" :class="seoPerformance.technical_seo?.https_usage === false ? 'ss-seo-health-poor' : ''" data-seo-metric="https-usage">
                <div class="ss-seo-hcard-left">
                  <span class="ss-seo-hcard-value ss-seo-hcard-value-sm">{{ seoPerformance.technical_seo?.https_usage === true ? '✓' : seoPerformance.technical_seo?.https_usage === false ? '✗' : '--' }}</span>
                </div>
                <div class="ss-seo-hcard-right">
                  <span class="ss-seo-hcard-label">HTTPS</span>
                  <span class="ss-seo-hcard-detail">{{ seoPerformance.technical_seo?.https_usage === true ? 'Enabled — traffic is encrypted' : seoPerformance.technical_seo?.https_usage === false ? 'Not enabled — SSL required' : 'Unknown' }}</span>
                </div>
              </div>

              <!-- Structured Data -->
              <div class="ss-seo-hcard" data-seo-metric="structured-data">
                <div class="ss-seo-hcard-left">
                  <span class="ss-seo-hcard-value ss-seo-hcard-value-sm">{{ seoPerformance.technical_seo?.structured_data === 'Detected' ? '✓' : seoPerformance.technical_seo?.structured_data ? '—' : '--' }}</span>
                </div>
                <div class="ss-seo-hcard-right">
                  <span class="ss-seo-hcard-label">Structured Data</span>
                  <span class="ss-seo-hcard-detail">{{ seoPerformance.technical_seo?.structured_data ?? 'Unknown' }} — JSON-LD schema markup</span>
                </div>
              </div>

              <!-- Stats row: Indexed Pages, Crawl Errors, Clicks, Impressions, CTR, Position -->
              <div class="ss-seo-stats-row">
                <div class="ss-seo-stat" data-seo-metric="indexed-pages">
                  <span class="ss-seo-stat-value">{{ seoPerformance.indexing_status?.indexed_pages ?? '--' }}</span>
                  <span class="ss-seo-stat-label">Indexed Pages</span>
                </div>
                <div class="ss-seo-stat" :class="seoCrawlErrorClass(seoPerformance.indexing_status?.crawl_errors)" data-seo-metric="crawl-errors">
                  <span class="ss-seo-stat-value">{{ seoPerformance.indexing_status?.crawl_errors ?? '--' }}</span>
                  <span class="ss-seo-stat-label">Crawl Errors</span>
                </div>
                <div class="ss-seo-stat" data-seo-metric="clicks">
                  <span class="ss-seo-stat-value">{{ seoPerformance.search_analytics?.total_clicks ?? '--' }}</span>
                  <span class="ss-seo-stat-label">Clicks ({{ seoPerformance.period }})</span>
                </div>
                <div class="ss-seo-stat" data-seo-metric="impressions">
                  <span class="ss-seo-stat-value">{{ seoPerformance.search_analytics?.total_impressions ?? '--' }}</span>
                  <span class="ss-seo-stat-label">Impressions</span>
                </div>
                <div class="ss-seo-stat" data-seo-metric="avg-ctr">
                  <span class="ss-seo-stat-value">{{ seoPerformance.search_analytics?.average_ctr != null ? seoPerformance.search_analytics.average_ctr + '%' : '--' }}</span>
                  <span class="ss-seo-stat-label">Avg CTR</span>
                </div>
                <div class="ss-seo-stat" data-seo-metric="avg-position">
                  <span class="ss-seo-stat-value">{{ seoPerformance.search_analytics?.average_position ?? '--' }}</span>
                  <span class="ss-seo-stat-label">Avg Position</span>
                </div>
              </div>

            </div>

            <!-- Action guidance — shown only when health issues are detected -->
            <div
              v-if="seoActionGuidance.length"
              class="ss-seo-guidance"
              data-seo-guidance
            >
              <p class="ss-seo-guidance-heading">Recommended actions</p>
              <ul class="ss-seo-guidance-list">
                <li
                  v-for="item in seoActionGuidance"
                  :key="item.key"
                  class="ss-seo-guidance-item"
                  :class="'ss-seo-guidance-' + item.severity"
                  :data-seo-guidance-key="item.key"
                >
                  <span class="ss-seo-guidance-msg">{{ item.message }}</span>
                  <router-link
                    v-if="item.prompt"
                    :to="{ name: 'editor', params: { id: $route.params.id }, query: { prompt: item.prompt, mode: 'change' } }"
                    class="ss-seo-fix-btn"
                  >Fix in editor →</router-link>
                </li>
              </ul>
            </div>

            <!-- Per-page metrics table -->
            <table class="ss-seo-table ss-seo-page-table">
              <thead>
                <tr>
                  <th class="ss-seo-th">Page</th>
                  <th class="ss-seo-th">Score</th>
                  <th class="ss-seo-th">Clicks</th>
                  <th class="ss-seo-th">Impressions</th>
                  <th class="ss-seo-th">CTR</th>
                  <th class="ss-seo-th">Position</th>
                  <th class="ss-seo-th">Action</th>
                </tr>
              </thead>
              <tbody v-if="seoRows.length">
                <tr
                  v-for="(row, idx) in seoRows"
                  :key="row.url || idx"
                  data-seo-page-row
                >
                  <td class="ss-seo-td ss-seo-td-url">{{ row.path || row.url || '--' }}</td>
                  <td class="ss-seo-td">{{ row.score ?? '--' }}</td>
                  <td class="ss-seo-td">{{ row.clicks ?? '--' }}</td>
                  <td class="ss-seo-td">{{ row.impressions ?? '--' }}</td>
                  <td class="ss-seo-td">{{ row.ctr != null ? row.ctr + '%' : '--' }}</td>
                  <td class="ss-seo-td">{{ row.position ?? '--' }}</td>
                  <td class="ss-seo-td">
                    <a
                      v-if="row.url"
                      :href="row.url"
                      target="_blank"
                      rel="noopener"
                      class="ss-seo-page-action"
                      data-seo-action="open-url"
                    >Open</a>
                    <span v-else class="ss-meta-muted">—</span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td class="ss-seo-td" colspan="7" data-seo-no-pages>
                    No per-page data available. Last crawl:
                    {{ seoPerformance.indexing_status?.last_crawl ? new Date(seoPerformance.indexing_status?.last_crawl).toLocaleDateString() : '--' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Analytics & Tracking — data-driven from /api/bwai/sites/:id/analytics -->
        <div class="ss-card" data-section="analytics">
          <div class="ss-card-head">
            <h2 class="ss-card-title">Analytics &amp; Tracking</h2>
          </div>

          <div v-if="analyticsLoading" class="ss-meta-muted" data-analytics-state="loading">Loading analytics…</div>
          <div v-else-if="analyticsError" class="ss-domain-error" data-analytics-state="error">{{ analyticsError }}</div>
          <div v-else class="ss-stack" data-analytics-state="ready">
            <!-- Amplitude — primary provider for this story -->
            <div class="ss-integration" data-provider="amplitude">
              <div class="ss-integration-left">
                <div class="ss-integration-icon" style="background:#1d4ed8">
                  <span>AM</span>
                </div>
                <div>
                  <p class="ss-integration-name">Amplitude</p>
                  <p class="ss-integration-desc">Product analytics with event tracking and funnels</p>
                </div>
              </div>
              <span class="ss-pill" :class="providerPillClass('amplitude')" data-provider-pill="amplitude">
                {{ providerStatusText('amplitude') }}
              </span>
            </div>

            <!-- Amplitude setup form -->
            <div v-if="canManageAnalytics" class="ss-domain-form" data-amplitude-form>
              <label class="ss-meta-muted" for="amplitude-api-key">Amplitude API key</label>
              <input
                id="amplitude-api-key"
                v-model="amplitudeForm.apiKey"
                class="ss-domain-input"
                type="text"
                placeholder="Your Amplitude project API key"
                :disabled="amplitudeSaving"
                @keyup.enter="saveAmplitude"
              />
              <label class="ss-meta-muted" for="amplitude-project-name">Project name (optional)</label>
              <input
                id="amplitude-project-name"
                v-model="amplitudeForm.projectName"
                class="ss-domain-input"
                type="text"
                placeholder="e.g. build-website-prod"
                :disabled="amplitudeSaving"
                @keyup.enter="saveAmplitude"
              />
              <label class="ss-meta-muted">
                <input type="checkbox" v-model="amplitudeForm.enabled" :disabled="amplitudeSaving" />
                Enabled (uncheck to keep config but stop tracking)
              </label>
              <div class="ss-domain-form-actions">
                <button
                  class="ss-btn ss-btn-primary ss-btn-sm"
                  :disabled="amplitudeSaving || !amplitudeForm.apiKey.trim()"
                  @click="saveAmplitude"
                  data-amplitude-save
                >
                  {{ amplitudeSaving ? 'Saving…' : 'Save Amplitude' }}
                </button>
              </div>
              <p v-if="amplitudeSuccess" class="ss-meta-verified" data-amplitude-success>{{ amplitudeSuccess }}</p>
              <p v-if="amplitudeError" class="ss-domain-error" data-amplitude-error>{{ amplitudeError }}</p>
              <ul v-if="amplitudeFieldErrors.length" class="ss-domain-error" data-amplitude-field-errors>
                <li v-for="e in amplitudeFieldErrors" :key="e.field">
                  <strong>{{ e.field }}:</strong> {{ e.message }}
                </li>
              </ul>
            </div>

            <!-- Microsoft Clarity (status reflects persisted state via AC009) -->
            <div class="ss-integration" data-provider="ms_clarity">
              <div class="ss-integration-left">
                <div class="ss-integration-icon" style="background:#2563eb">
                  <span>MC</span>
                </div>
                <div>
                  <p class="ss-integration-name">Microsoft Clarity</p>
                  <p class="ss-integration-desc">User behavior and heatmaps</p>
                </div>
              </div>
              <span class="ss-pill" :class="providerPillClass('ms_clarity')" data-provider-pill="ms_clarity">
                {{ providerStatusText('ms_clarity') }}
              </span>
            </div>

            <!-- Google Analytics -->
            <div class="ss-integration" data-provider="google_analytics">
              <div class="ss-integration-left">
                <div class="ss-integration-icon" style="background:#f97316">
                  <span>GA</span>
                </div>
                <div>
                  <p class="ss-integration-name">Google Analytics</p>
                  <p class="ss-integration-desc">Traffic and user insights</p>
                </div>
              </div>
              <span class="ss-pill" :class="providerPillClass('google_analytics')" data-provider-pill="google_analytics">
                {{ providerStatusText('google_analytics') }}
              </span>
            </div>

            <!-- Google Analytics setup form. The numeric GA4 property id powers
                 the Growth dashboard's server-side read-back (social referral +
                 lead conversions); the measurement id drives client tracking. -->
            <div v-if="canManageAnalytics" class="ss-domain-form" data-ga-form>
              <label class="ss-meta-muted" for="ga-tracking-id">Measurement ID</label>
              <input
                id="ga-tracking-id"
                v-model="gaForm.trackingId"
                class="ss-domain-input"
                type="text"
                placeholder="G-XXXXXXXXXX"
                :disabled="gaSaving"
                @keyup.enter="saveGoogleAnalytics"
              />
              <label class="ss-meta-muted" for="ga-property-id">GA4 Property ID (for Growth dashboard read-back)</label>
              <input
                id="ga-property-id"
                v-model="gaForm.propertyId"
                class="ss-domain-input"
                type="text"
                inputmode="numeric"
                placeholder="e.g. 123456789"
                :disabled="gaSaving"
                @keyup.enter="saveGoogleAnalytics"
              />
              <label class="ss-meta-muted">
                <input type="checkbox" v-model="gaForm.enabled" :disabled="gaSaving" />
                Enabled (uncheck to keep config but stop read-back)
              </label>
              <div class="ss-domain-form-actions">
                <button
                  class="ss-btn ss-btn-primary ss-btn-sm"
                  :disabled="gaSaving || !gaForm.trackingId.trim()"
                  @click="saveGoogleAnalytics"
                  data-ga-save
                >
                  {{ gaSaving ? 'Saving…' : 'Save Google Analytics' }}
                </button>
              </div>
              <p v-if="gaSuccess" class="ss-meta-verified" data-ga-success>{{ gaSuccess }}</p>
              <p v-if="gaError" class="ss-domain-error" data-ga-error>{{ gaError }}</p>
              <ul v-if="gaFieldErrors.length" class="ss-domain-error" data-ga-field-errors>
                <li v-for="e in gaFieldErrors" :key="e.field">
                  <strong>{{ e.field }}:</strong> {{ e.message }}
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>

      <!-- Error State -->
      <div v-else-if="error" class="ss-error-state">
        <svg class="ss-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="ss-error-title">Failed to load site settings</p>
        <p class="ss-error-detail">{{ error }}</p>
        <button
          @click="refreshSettings"
          class="ss-btn ss-btn-primary"
        >
          Try Again
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '../utils/api'
import { useSitesStore } from '../stores/sites'
import { normalizeSeoPageMetrics } from './site-settings-seo-metrics.js'
import SiteOverview from '../components/SiteOverview.vue'

export default {
  name: 'SiteSettings',
  components: { SiteOverview },
  setup() {
    const route = useRoute()
    const sitesStore = useSitesStore()

    const canManageLogo = computed(() => {
      const u = sitesStore.user
      return !!(u?.isAdmin || u?.implementationAccess)
    })
    const settings = ref(null)
    const loading = ref(false)
    const error = ref(null)
    const logoUploading = ref(false)
    const logoStatus = ref({ message: '', type: '' })
    const showRemoveConfirm = ref(false)
    const logoFileInput = ref(null)
    const logoPreviewBroken = ref(false)

    let logoStatusTimer = null
    const clearLogoStatus = (delay = 5000) => {
      clearTimeout(logoStatusTimer)
      logoStatusTimer = setTimeout(() => { logoStatus.value = { message: '', type: '' } }, delay)
    }

    const logoPreviewUrl = computed(() => {
      if (!settings.value?.logo?.renditions?.length) return ''
      if (logoPreviewBroken.value) return ''
      const medium = settings.value.logo.renditions.find(r => r.name === 'medium' || r.name === 'large')
      return medium?.url || settings.value.logo.renditions[0]?.url || ''
    })

    const onLogoPreviewError = () => { logoPreviewBroken.value = true }

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    }

    const handleLogoUpload = async (event) => {
      const file = event.target.files?.[0]
      if (!file) return

      const siteId = route.params.id
      logoUploading.value = true
      logoStatus.value = { message: '', type: '' }
      logoPreviewBroken.value = false

      try {
        const formData = new FormData()
        formData.append('logo', file)
        const response = await api.post(`/sites/${siteId}/logo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        settings.value.logo = response.data.logo
        if (response.data.refreshFailed) {
          logoStatus.value = { message: 'Logo uploaded, but asset refresh failed. The site preview may still show the old logo.', type: 'error' }
          clearLogoStatus(8000)
        } else {
          logoStatus.value = { message: 'Logo uploaded successfully', type: 'success' }
          clearLogoStatus()
        }
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to upload logo'
        logoStatus.value = { message: msg, type: 'error' }
        clearLogoStatus(8000)
      } finally {
        logoUploading.value = false
        if (logoFileInput.value) logoFileInput.value.value = ''
      }
    }

    const handleLogoRemove = () => {
      showRemoveConfirm.value = true
    }

    const confirmLogoRemove = async () => {
      const siteId = route.params.id
      logoUploading.value = true
      logoStatus.value = { message: '', type: '' }
      showRemoveConfirm.value = false

      try {
        await api.delete(`/sites/${siteId}/logo`)
        settings.value.logo = null
        logoStatus.value = { message: 'Logo removed', type: 'success' }
        clearLogoStatus()
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to remove logo'
        logoStatus.value = { message: msg, type: 'error' }
        clearLogoStatus(8000)
      } finally {
        logoUploading.value = false
      }
    }

    const fetchSettings = async () => {
      const siteId = route.params.id
      if (!siteId) return

      loading.value = true
      error.value = null

      try {
        const response = await api.get(`/sites/${siteId}/settings`)
        settings.value = response.data.settings
      } catch (err) {
        console.error('Failed to fetch site settings:', err)
        error.value = err.response?.data?.message || 'Failed to load settings'
      } finally {
        loading.value = false
      }
    }

    const refreshSettings = () => {
      fetchSettings()
      fetchSeoPerformance()
    }

    const domainDotClass = () => {
      if (!settings.value?.domain_status) return 'ss-dot-neutral'
      return settings.value.domain_status.hasCustomDomain ? 'ss-dot-success' : 'ss-dot-info'
    }

    const getDomainStatusText = () => {
      if (!settings.value?.domain_status) return 'Unknown'
      return settings.value.domain_status.hasCustomDomain ? 'Custom Domain Active' : 'Using Default Domain'
    }

    const getSSLStatusClass = () => {
      const sslStatus = settings.value?.domain_status?.domains?.[0]?.ssl_status
      if (sslStatus === 'active') return 'ss-meta-verified'
      if (sslStatus === 'pending') return 'ss-meta-pending'
      return 'ss-meta-muted'
    }

    const getSSLStatusText = () => {
      const sslStatus = settings.value?.domain_status?.domains?.[0]?.ssl_status
      return sslStatus ? sslStatus.charAt(0).toUpperCase() + sslStatus.slice(1) : 'Unknown'
    }

    // Deployment state → pill/dot styling + human text. These states come from
    // the backend's honest mapping (live | built | building | failed | canceled
    // | skipped), so 'building' is never dressed up as success.
    const deployPillClass = (state) => ({
      live: 'ss-pill-success', built: 'ss-pill-info', building: 'ss-pill-warning',
      failed: 'ss-pill-danger', canceled: 'ss-pill-neutral', skipped: 'ss-pill-neutral',
    }[state] || 'ss-pill-neutral')

    const deployDotClass = (state) => ({
      live: 'ss-dot-success', built: 'ss-dot-info', building: 'ss-dot-warning',
      failed: 'ss-dot-danger',
    }[state] || 'ss-dot-neutral')

    const deployStateText = (state, stage) => {
      if (state === 'building') return stage ? `Building · ${stage}` : 'Building'
      return { live: 'Live', built: 'Built', failed: 'Failed', canceled: 'Canceled', skipped: 'Skipped' }[state]
        || (state ? state.charAt(0).toUpperCase() + state.slice(1) : 'Unknown')
    }

    const deployHeaderText = computed(() => {
      const info = settings.value?.deployment_info
      if (!info?.cloudflare_enabled) return 'Not connected'
      if (info.pending?.state === 'building') return 'Deploying…'
      if (info.pending?.state === 'failed') return 'Last deploy failed'
      if (info.live) return 'Live'
      return 'No deployment yet'
    })
    const deployHeaderDotClass = computed(() => {
      const info = settings.value?.deployment_info
      if (!info?.cloudflare_enabled) return 'ss-dot-neutral'
      if (info.pending?.state === 'building') return 'ss-dot-warning'
      if (info.pending?.state === 'failed') return 'ss-dot-danger'
      if (info.live) return 'ss-dot-success'
      return 'ss-dot-neutral'
    })

    const formatDeploymentTime = (timestamp) => {
      if (!timestamp) return 'Unknown'
      return new Date(timestamp).toLocaleString()
    }

    const canManageDomain = computed(() => {
      const u = sitesStore.user
      return !!(u?.isAdmin || u?.implementationAccess)
    })

    const DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i

    const showDomainForm = ref(false)
    const domainInput = ref('')
    const domainAdding = ref(false)
    const domainRemoving = ref(new Set())
    const domainConfirmRemove = ref('')
    const domainError = ref('')
    const domainSuccess = ref('')

    // Analytics — AC005 / AC006 / AC007 from the Amplitude story.
    // The analytics card reads from GET /sites/:id/analytics and writes
    // through the per-provider POST endpoints. State is held here so the
    // success path can refresh without a page reload (AC006), and the
    const seoPerformance = ref(null)
    const seoRows = ref([])
    const seoLoading = ref(false)
    const seoError = ref('')
    const seoLastRun = ref(null)
    let _seoRequestSeq = 0
    const seoRefreshing = ref(false)
    const seoRefreshProgress = ref(0)
    const seoRefreshFailed = ref(false)
    const seoRefreshDone = ref(false)
    let _seoRefreshTimer = null
    let _seoRefreshRunId = null
    let _seoRefreshPollCount = 0

    const fetchSeoPerformance = async () => {
      const siteId = route.params.id
      if (!siteId) return
      const seq = ++_seoRequestSeq
      seoLoading.value = true
      seoError.value = ''
      seoPerformance.value = null
      seoRows.value = []
      try {
        const response = await api.get(`/sites/${siteId}/seo/performance`, { params: { period: '30d' } })
        if (seq !== _seoRequestSeq) return
        seoPerformance.value = response.data.performance || null
        seoRows.value = normalizeSeoPageMetrics(response.data)
        seoLastRun.value = response.data.last_run || null
      } catch (err) {
        if (seq !== _seoRequestSeq) return
        console.error('Failed to fetch SEO performance:', err)
        seoError.value = err.response?.data?.error
          || err.response?.data?.message
          || 'Failed to load SEO data'
      } finally {
        if (seq === _seoRequestSeq) seoLoading.value = false
      }
    }

    const seoLastRefreshedLabel = computed(() => {
      const at = seoPerformance.value?.analyzed_at || seoLastRun.value?.last_run_at || seoPerformance.value?.collected_at
      if (!at) return null
      const d = new Date(at)
      if (isNaN(d.getTime())) return null
      const diff = Date.now() - d.getTime()
      if (diff < 60000) return 'just now'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
      return d.toLocaleDateString()
    })

    const _finishRefresh = async (failed = false) => {
      if (_seoRefreshTimer) { clearInterval(_seoRefreshTimer); _seoRefreshTimer = null }
      seoRefreshProgress.value = 100
      seoRefreshFailed.value = failed
      seoRefreshDone.value = true
      seoRefreshing.value = false
      if (!failed) await fetchSeoPerformance()
      setTimeout(() => { seoRefreshDone.value = false; seoRefreshProgress.value = 0 }, 2000)
    }

    const triggerSeoRefresh = async () => {
      const siteId = route.params.id
      if (!siteId || seoRefreshing.value) return
      seoRefreshing.value = true
      seoRefreshProgress.value = 5
      seoRefreshFailed.value = false
      seoRefreshDone.value = false
      _seoRefreshRunId = null
      _seoRefreshPollCount = 0
      if (_seoRefreshTimer) { clearInterval(_seoRefreshTimer); _seoRefreshTimer = null }

      try {
        const enqRes = await api.post(`/sites/${siteId}/seo/recollect`, { period: '30d' })
        _seoRefreshRunId = enqRes.data?.run?.id || null
      } catch (err) {
        await _finishRefresh(true)
        return
      }

      _seoRefreshTimer = setInterval(async () => {
        _seoRefreshPollCount++
        // After 60s (30 polls × 2s) give up and reload whatever data exists
        if (_seoRefreshPollCount >= 30) {
          await _finishRefresh(false)
          return
        }
        if (seoRefreshProgress.value < 88) {
          seoRefreshProgress.value = Math.min(88, seoRefreshProgress.value + Math.ceil((88 - seoRefreshProgress.value) / 6))
        }
        if (!_seoRefreshRunId) return
        try {
          const statusRes = await api.get(`/sites/${siteId}/seo/runs/${_seoRefreshRunId}`)
          const status = statusRes.data?.run?.status
          if (status === 'succeeded' || status === 'failed') {
            await _finishRefresh(status === 'failed')
          }
        } catch (_) { /* keep polling */ }
      }, 2000)
    }

    const SEO_WEIGHTS_MAX = { title: 20, description: 15, h1: 15, canonical: 10, viewport: 10, https: 10, structured: 10, alt: 10 }
    const SEO_CRITERION_LABELS = { title: 'Title', description: 'Description', h1: 'Heading (H1)', canonical: 'Canonical URL', viewport: 'Viewport', https: 'HTTPS', structured: 'Structured Data', alt: 'Image Alt' }

    const seoBreakdownRows = computed(() => {
      const bd = seoPerformance.value?.technical_seo?.score_breakdown
      if (!bd) return []
      return Object.keys(SEO_WEIGHTS_MAX).map(key => ({
        key,
        label: SEO_CRITERION_LABELS[key] || key,
        earned: bd[key] ?? 0,
        max: SEO_WEIGHTS_MAX[key],
      }))
    })

    // Local-analyzer story AC005: the primary scorecard is the locally
    // computed Technical SEO Score. Prefer `technical_score` (new), fall
    // back to `page_speed_score` (legacy provider-derived) so the card
    // still renders against older snapshot payloads.
    const seoTechnicalScore = computed(() => {
      const ts = seoPerformance.value?.technical_seo
      if (!ts) return null
      if (ts.technical_score != null) return ts.technical_score
      if (ts.page_speed_score != null) return ts.page_speed_score
      return null
    })

    // AEO/GEO story AC009: surface the two additional canonical scores +
    // their explainable breakdown rows. Each breakdown row carries
    // { key, label, passed (true|'partial'|false), value, weight, reason? }
    // so the UI can render bars and reason codes without any inference.
    const aeoScore = computed(() => seoPerformance.value?.technical_seo?.aeo_score ?? null)
    const geoScore = computed(() => seoPerformance.value?.technical_seo?.geo_score ?? null)

    const AEO_CHECK_LABELS = {
      faq_schema: 'FAQ / HowTo schema',
      qa_pattern: 'Q&A heading pattern',
      definition_lists: 'Definition lists',
      semantic_landmarks: 'Semantic landmarks',
      scannability: 'Lists & tables',
      article_schema: 'Article schema',
    }
    const GEO_CHECK_LABELS = {
      llms_txt: '/llms.txt present',
      ai_bot_allowlist: 'AI bots allowed in robots.txt',
      content_depth: 'Content depth',
      external_citations: 'External citations',
      freshness: 'Freshness meta',
      entity_density: 'Entity density',
      canonical_url: 'Self-referential canonical',
    }
    function breakdownRows(block, labels) {
      if (!block || typeof block !== 'object') return []
      return Object.keys(labels).map(key => {
        const entry = block[key] || {}
        return {
          key,
          label: labels[key],
          passed: entry.passed === true ? true : entry.passed === 'partial' ? 'partial' : false,
          value: Number.isFinite(entry.value) ? entry.value : 0,
          weight: Number.isFinite(entry.weight) ? entry.weight : 0,
          reason: typeof entry.reason === 'string' ? entry.reason : null,
        }
      })
    }
    const aeoBreakdownRows = computed(() => breakdownRows(seoPerformance.value?.technical_seo?.aeo_breakdown, AEO_CHECK_LABELS))
    const geoBreakdownRows = computed(() => breakdownRows(seoPerformance.value?.technical_seo?.geo_breakdown, GEO_CHECK_LABELS))

    // AC010 (AEO/GEO story): expose the calibration block to the template.
    // When the API didn't include one (legacy snapshots), default to a
    // 'disabled' shape so the UI can still render the badge in a
    // labeled-disabled state without conditionals everywhere.
    const calibrationStatus = computed(() => {
      const c = seoPerformance.value?.technical_seo?.calibration
      if (!c || typeof c !== 'object') {
        return { status: 'disabled', source: null, fetched_at: null,
          aeo_delta: null, geo_delta: null, technical_delta: null, error: null }
      }
      return c
    })

    // True only when the API returned success and at least one metric group
    // contains a non-null value, so "all-null" responses fall to empty state.
    // Local-analyzer story AC007: the Technical SEO Score alone is enough
    // to render the ready state — search-analytics fields may be null
    // because the local analyzer does not produce them. The first OR-arm
    // (`ts.technical_score != null`) is exactly the AC007 condition.
    const seoHasData = computed(() => {
      const p = seoPerformance.value
      if (!p) return false
      const sa = p.search_analytics
      const ts = p.technical_seo
      const is_ = p.indexing_status
      // AEO/GEO story AC009: any of the three canonical in-house scores
      // unlocks the ready state. Calibration, search-analytics, indexing
      // fields are still permitted to be null.
      return (
        (ts && (ts.technical_score != null || ts.page_speed_score != null ||
                ts.aeo_score != null || ts.geo_score != null)) ||
        (sa && (sa.total_clicks != null || sa.total_impressions != null)) ||
        (is_ && is_.indexed_pages != null)
      )
    })

    // Produces an ordered list of action items for metrics that are in a poor
    // or warning state. Each item: { key, message, severity ('warn'|'poor') }
    const seoActionGuidance = computed(() => {
      const p = seoPerformance.value
      if (!p) return []
      const actions = []
      const ts = p.technical_seo
      const sa = p.search_analytics
      const is_ = p.indexing_status

      if (ts) {
        const score = ts.technical_score ?? ts.page_speed_score
        if (score != null && score < 50) {
          actions.push({ key: 'technical_seo_poor', severity: 'poor',
            message: `Technical SEO Score is ${score}/100 — fix missing titles, meta descriptions, and canonical tags first.`,
            prompt: 'Fix the SEO on every page: add a unique descriptive title (10-70 characters), a meta description (50-160 characters), exactly one H1 heading, a canonical link tag, and add JSON-LD structured data schema markup to the homepage.' })
        } else if (score != null && score < 90) {
          actions.push({ key: 'technical_seo_warn', severity: 'warn',
            message: `Technical SEO Score is ${score}/100 — tighten title/description lengths and add structured data.`,
            prompt: 'Improve SEO: ensure every page has a unique descriptive title between 10-70 characters, a meta description between 50-160 characters, and add JSON-LD structured data schema markup to improve search visibility.' })
        }
        if (ts.mobile_friendliness && ts.mobile_friendliness !== 'Good') {
          actions.push({ key: 'mobile', severity: 'warn',
            message: `Mobile friendliness is "${ts.mobile_friendliness}" — test your site with Google's mobile-friendly tool.`,
            prompt: 'Make the website fully mobile-friendly: add the viewport meta tag to every page, ensure all text is readable without zooming, buttons are large enough to tap, and the layout adapts correctly on small screens.' })
        }
        if (ts.https_usage === false) {
          actions.push({ key: 'https', severity: 'poor',
            message: 'HTTPS is not enabled — secure your site with an SSL certificate to avoid ranking penalties.',
            prompt: null })
        }
      }

      if (is_) {
        const errors = is_.crawl_errors
        if (errors != null && errors >= 5) {
          actions.push({ key: 'crawl_errors_poor', severity: 'poor',
            message: `${errors} crawl errors detected — review your robots.txt, redirects, and broken links.`,
            prompt: 'Fix crawl errors: find and fix broken internal links, set up proper 301 redirects for moved pages, and make sure no important pages are accidentally blocked in robots.txt.' })
        } else if (errors != null && errors > 0) {
          actions.push({ key: 'crawl_errors_warn', severity: 'warn',
            message: `${errors} crawl error${errors === 1 ? '' : 's'} detected — check for broken links or blocked resources.`,
            prompt: 'Fix broken links and crawl errors: find any internal links pointing to missing pages and either fix or remove them.' })
        }
      }

      if (sa) {
        const ctr = sa.average_ctr
        if (ctr != null && ctr < 2) {
          actions.push({ key: 'low_ctr', severity: 'warn',
            message: `Average CTR is ${ctr}% — improve title tags and meta descriptions to increase click-through rates.`,
            prompt: 'Rewrite the title tags and meta descriptions for each page to be more compelling and click-worthy, while keeping titles under 60 characters and descriptions under 160 characters. Focus on clearly communicating the value proposition.' })
        }
      }

      // AEO/GEO story AC009: surface failed/partial sub-checks as
      // remediation items. The check key + reason code drive a concrete
      // copy/prompt pair the user can act on. Keyed `aeo:<check>` /
      // `geo:<check>` so the UI can target them in tests.
      const aeo = ts?.aeo_breakdown
      const geo = ts?.geo_breakdown
      const aeoScoreVal = ts?.aeo_score
      if (aeoScoreVal != null && aeoScoreVal < 70) {
        actions.push({ key: 'aeo_score_low', severity: aeoScoreVal < 40 ? 'poor' : 'warn',
          message: `AEO Score is ${aeoScoreVal}/100 — answer-engine readiness is below the recommended bar.`,
          prompt: 'Improve AEO: add FAQ/HowTo JSON-LD schema, format key sections as question-shaped H2 headings followed by short answer paragraphs, and use semantic landmarks (<main>, <article>, <section>) throughout.' })
      }
      if (aeo) {
        if (aeo.faq_schema && aeo.faq_schema.passed === false) actions.push({ key: 'aeo:faq_schema', severity: 'warn',
          message: 'No FAQ/HowTo schema detected — answer engines miss easy structured cites.',
          prompt: 'Add FAQPage or HowTo JSON-LD schema to pages with question/answer content.' })
        if (aeo.qa_pattern && aeo.qa_pattern.passed === false) actions.push({ key: 'aeo:qa_pattern', severity: 'warn',
          message: 'No question-shaped headings — answer engines surface Q&A patterns first.',
          prompt: 'Reformat key sections with H2 questions like "What is X?" or "How do I Y?" followed by 2-3 sentence answers.' })
        if (aeo.semantic_landmarks && aeo.semantic_landmarks.passed === false) actions.push({ key: 'aeo:semantic_landmarks', severity: 'warn',
          message: 'Missing semantic landmarks — answer engines lean on <main>, <article>, <section> to extract content.',
          prompt: 'Wrap the page content in semantic HTML landmarks (<main>, <article>, <section>, <nav>) instead of generic <div> blocks.' })
      }
      const geoScoreVal = ts?.geo_score
      if (geoScoreVal != null && geoScoreVal < 70) {
        actions.push({ key: 'geo_score_low', severity: geoScoreVal < 40 ? 'poor' : 'warn',
          message: `GEO Score is ${geoScoreVal}/100 — generative-engine citation readiness is below the recommended bar.`,
          prompt: 'Improve GEO: publish /llms.txt at the site root, allow GPTBot/PerplexityBot/ClaudeBot/Google-Extended in robots.txt, add article:published_time meta to articles, and expand sparse pages above 300 words.' })
      }
      if (geo) {
        if (geo.llms_txt && geo.llms_txt.passed === false) actions.push({ key: 'geo:llms_txt', severity: 'warn',
          message: 'No /llms.txt published — LLM crawlers can\'t find a content map.',
          prompt: 'Publish an /llms.txt file at the site root following the llmstxt.org convention, listing your most important pages with one-line descriptions.' })
        if (geo.ai_bot_allowlist && geo.ai_bot_allowlist.passed === false) actions.push({ key: 'geo:ai_bot_allowlist', severity: 'warn',
          message: 'AI bots not explicitly allowed in robots.txt — they may skip your site.',
          prompt: 'Update /robots.txt to add explicit User-agent sections for GPTBot, PerplexityBot, ClaudeBot, and Google-Extended with Allow: / rules.' })
        if (geo.freshness && geo.freshness.passed === false) actions.push({ key: 'geo:freshness', severity: 'warn',
          message: 'No freshness signals — LLM engines prefer recently-updated content.',
          prompt: 'Add <meta property="article:published_time"> and <meta property="article:modified_time"> tags to article pages, with ISO-8601 timestamps.' })
      }

      return actions
    })

    // Returns a CSS modifier class based on a 0-100 score:
    //   ≥ 90 → success,  ≥ 50 → warning,  < 50 or null → danger
    const seoScoreClass = (score) => {
      if (score == null) return 'ss-seo-health-neutral'
      if (score >= 90) return 'ss-seo-health-good'
      if (score >= 50) return 'ss-seo-health-warn'
      return 'ss-seo-health-poor'
    }

    // Returns a CSS modifier class for crawl-error count:
    //   0 → success, 1-4 → warning, ≥ 5 → danger
    const seoCrawlErrorClass = (count) => {
      if (count == null) return 'ss-seo-health-neutral'
      if (count === 0) return 'ss-seo-health-good'
      if (count < 5) return 'ss-seo-health-warn'
      return 'ss-seo-health-poor'
    }

    // ── Recommendations (Action-to-Outcome Feedback Loop, BUI-F-16) ──────
    // Ranked list is served pre-sorted with per-item rationale; the component
    // just renders it. Marking a recommendation done logs an executed event so
    // the backend can measure the post-action outcome.
    const recommendations = ref([])
    const recRankingMode = ref('default')
    const recLearningActive = ref(false)
    const recLoading = ref(false)
    const recError = ref('')
    const recActionPending = ref(new Set())

    const fetchRecommendations = async () => {
      const siteId = route.params.id
      if (!siteId) return
      recLoading.value = true
      recError.value = ''
      try {
        const response = await api.get(`/sites/${siteId}/recommendations`)
        recommendations.value = Array.isArray(response.data.recommendations)
          ? response.data.recommendations
          : []
        recRankingMode.value = response.data.ranking_mode || 'default'
        recLearningActive.value = response.data.learning_active === true
      } catch (err) {
        console.error('Failed to fetch recommendations:', err)
        recError.value = err.response?.data?.error
          || err.response?.data?.message
          || 'Failed to load recommendations'
      } finally {
        recLoading.value = false
      }
    }

    const markRecommendationDone = async (rec) => {
      const siteId = route.params.id
      if (!siteId || !rec || recActionPending.value.has(rec.recommendation_id)) return
      // Vue reactivity for Set: reassign a new Set.
      recActionPending.value = new Set(recActionPending.value).add(rec.recommendation_id)
      try {
        await api.post(`/recommendations/${rec.recommendation_id}/events`, {
          event_type: 'executed',
          evidence: { userConfirmed: true },
        })
        // Executed recs drop out of the active list — refetch to reflect it.
        await fetchRecommendations()
      } catch (err) {
        console.error('Failed to mark recommendation done:', err)
        recError.value = err.response?.data?.error
          || err.response?.data?.message
          || 'Failed to update recommendation'
      } finally {
        const next = new Set(recActionPending.value)
        next.delete(rec.recommendation_id)
        recActionPending.value = next
      }
    }

    // failure path can keep entered values for retry (AC007).
    const analytics = ref(null)
    const analyticsLoading = ref(false)
    const analyticsError = ref('')
    const amplitudeForm = ref({ apiKey: '', projectName: '', enabled: true })
    const amplitudeSaving = ref(false)
    const amplitudeSuccess = ref('')
    const amplitudeError = ref('')
    const amplitudeFieldErrors = ref([])

    const gaForm = ref({ trackingId: '', propertyId: '', enabled: true })
    const gaSaving = ref(false)
    const gaSuccess = ref('')
    const gaError = ref('')
    const gaFieldErrors = ref([])
    let amplitudeStatusTimer = null

    const canManageAnalytics = computed(() => {
      const u = sitesStore.user
      return !!(u?.isAdmin || u?.implementationAccess)
    })

    const fetchAnalytics = async () => {
      const siteId = route.params.id
      if (!siteId) return
      analyticsLoading.value = true
      analyticsError.value = ''
      try {
        const response = await api.get(`/sites/${siteId}/analytics`)
        analytics.value = response.data.analytics || null
        // Seed the form from persisted state so the user sees their saved
        // values when they return to the page.
        const amp = analytics.value && analytics.value.providers && analytics.value.providers.amplitude
        if (amp) {
          amplitudeForm.value = {
            apiKey: amp.api_key || '',
            projectName: amp.project_name || '',
            enabled: amp.enabled !== false,
          }
        }
        const ga = analytics.value && analytics.value.providers && analytics.value.providers.google_analytics
        if (ga) {
          gaForm.value = {
            trackingId: ga.tracking_id || '',
            propertyId: ga.property_id || '',
            enabled: ga.enabled !== false,
          }
        }
      } catch (err) {
        console.error('Failed to fetch analytics:', err)
        analyticsError.value = err.response?.data?.error
          || err.response?.data?.message
          || 'Failed to load analytics'
      } finally {
        analyticsLoading.value = false
      }
    }

    const providerStatusText = (key) => {
      const p = analytics.value && analytics.value.providers && analytics.value.providers[key]
      if (!p) return 'Setup Required'
      if (p.enabled === true) return 'Active'
      if (p.status === 'disabled') return 'Disabled'
      return 'Setup Required'
    }

    const providerPillClass = (key) => {
      const p = analytics.value && analytics.value.providers && analytics.value.providers[key]
      if (p && p.enabled === true) return 'ss-pill-success'
      if (p && p.status === 'disabled') return 'ss-pill-warning'
      return 'ss-pill-neutral'
    }

    const clearAmplitudeStatusLater = (delay = 5000) => {
      clearTimeout(amplitudeStatusTimer)
      amplitudeStatusTimer = setTimeout(() => { amplitudeSuccess.value = '' }, delay)
    }

    const saveAmplitude = async () => {
      const siteId = route.params.id
      if (!siteId) return
      // Clear PRIOR success/error before attempting — don't show stale state.
      amplitudeSuccess.value = ''
      amplitudeError.value = ''
      amplitudeFieldErrors.value = []
      amplitudeSaving.value = true
      try {
        const payload = {
          apiKey: amplitudeForm.value.apiKey.trim(),
          enabled: amplitudeForm.value.enabled === true,
        }
        const projectName = amplitudeForm.value.projectName.trim()
        if (projectName) payload.projectName = projectName

        const response = await api.post(`/sites/${siteId}/analytics/amplitude`, payload)
        // Re-read so the pill (Active / Disabled) updates from authoritative
        // state, not from a hopeful local merge. AC006 wants visible update
        // without a manual refresh.
        await fetchAnalytics()
        amplitudeSuccess.value = response.data?.message
          || 'Amplitude analytics saved.'
        clearAmplitudeStatusLater()
      } catch (err) {
        // AC007: actionable error text, no false success, user's entered
        // values preserved (we deliberately do NOT clear amplitudeForm here).
        const data = err.response?.data || {}
        if (Array.isArray(data.details)) {
          amplitudeFieldErrors.value = data.details
            .filter((d) => d && d.field && d.message)
            .map((d) => ({ field: d.field, message: d.message }))
        }
        amplitudeError.value = data.error
          || data.message
          || 'Failed to save Amplitude settings. Please try again.'
      } finally {
        amplitudeSaving.value = false
      }
    }

    const saveGoogleAnalytics = async () => {
      const siteId = route.params.id
      if (!siteId) return
      gaSuccess.value = ''
      gaError.value = ''
      gaFieldErrors.value = []
      gaSaving.value = true
      try {
        const payload = {
          trackingId: gaForm.value.trackingId.trim(),
          enabled: gaForm.value.enabled === true,
        }
        const propertyId = gaForm.value.propertyId.trim()
        if (propertyId) payload.propertyId = propertyId

        const response = await api.post(`/sites/${siteId}/analytics/google-analytics`, payload)
        await fetchAnalytics()
        gaSuccess.value = response.data?.message || 'Google Analytics saved.'
      } catch (err) {
        const data = err.response?.data || {}
        if (Array.isArray(data.details)) {
          gaFieldErrors.value = data.details
            .filter((d) => d && d.field && d.message)
            .map((d) => ({ field: d.field, message: d.message }))
        }
        gaError.value = data.error || data.message || 'Failed to save Google Analytics settings. Please try again.'
      } finally {
        gaSaving.value = false
      }
    }

    const addDomain = async () => {
      const siteId = route.params.id
      const name = domainInput.value.trim().toLowerCase()
      if (!name) return
      if (!DOMAIN_RE.test(name)) {
        domainError.value = 'Invalid domain format. Use e.g. www.example.com — no http:// prefix.'
        return
      }
      domainError.value = ''
      domainSuccess.value = ''
      domainAdding.value = true
      try {
        await api.post(`/sites/${siteId}/domains`, { domain: name })
        domainInput.value = ''
        showDomainForm.value = false
        domainSuccess.value = `Domain "${name}" added. DNS setup instructions are shown below.`
        await fetchSettings()
        setTimeout(() => { domainSuccess.value = '' }, 8000)
      } catch (err) {
        const data = err.response?.data
        domainError.value = data?.errors?.[0]?.msg || data?.error || data?.message || 'Failed to add domain'
      } finally {
        domainAdding.value = false
      }
    }

    const confirmRemoveDomain = (domainName) => { domainConfirmRemove.value = domainName }
    const cancelRemoveDomain = () => { domainConfirmRemove.value = '' }

    const removeDomain = async (domainName) => {
      const siteId = route.params.id
      domainConfirmRemove.value = ''
      domainRemoving.value = new Set([...domainRemoving.value, domainName])
      domainError.value = ''
      domainSuccess.value = ''
      try {
        await api.delete(`/sites/${siteId}/domains/${encodeURIComponent(domainName)}`)
        domainSuccess.value = `Domain "${domainName}" removed.`
        await fetchSettings()
        setTimeout(() => { domainSuccess.value = '' }, 5000)
      } catch (err) {
        domainError.value = err.response?.data?.error || 'Failed to remove domain'
      } finally {
        const next = new Set(domainRemoving.value)
        next.delete(domainName)
        domainRemoving.value = next
      }
    }

    const copyDnsValue = async (value) => {
      try {
        await navigator.clipboard.writeText(value)
        domainSuccess.value = 'Copied to clipboard!'
        setTimeout(() => { domainSuccess.value = '' }, 3000)
      } catch (_) {}
    }

    onMounted(() => {
      fetchSettings()
      fetchSeoPerformance()
      fetchRecommendations()
      fetchAnalytics()
    })

    return {
      settings,
      loading,
      error,
      refreshSettings,
      domainDotClass,
      getDomainStatusText,
      getSSLStatusClass,
      getSSLStatusText,
      deployPillClass,
      deployDotClass,
      deployStateText,
      deployHeaderText,
      deployHeaderDotClass,
      formatDeploymentTime,
      logoUploading,
      logoStatus,
      logoPreviewUrl,
      logoPreviewBroken,
      showRemoveConfirm,
      logoFileInput,
      onLogoPreviewError,
      formatFileSize,
      handleLogoUpload,
      handleLogoRemove,
      confirmLogoRemove,
      canManageLogo,
      canManageDomain,
      showDomainForm,
      domainInput,
      domainAdding,
      domainRemoving,
      domainConfirmRemove,
      domainError,
      domainSuccess,
      addDomain,
      confirmRemoveDomain,
      cancelRemoveDomain,
      removeDomain,
      copyDnsValue,
      seoPerformance,
      seoRows,
      seoHasData,
      seoTechnicalScore,
      aeoScore,
      geoScore,
      aeoBreakdownRows,
      geoBreakdownRows,
      calibrationStatus,
      seoLoading,
      seoError,
      seoScoreClass,
      seoCrawlErrorClass,
      seoActionGuidance,
      seoLastRun,
      seoLastRefreshedLabel,
      seoRefreshing,
      seoRefreshProgress,
      seoRefreshFailed,
      seoRefreshDone,
      triggerSeoRefresh,
      seoBreakdownRows,
      analytics,
      analyticsLoading,
      analyticsError,
      amplitudeForm,
      amplitudeSaving,
      amplitudeSuccess,
      amplitudeError,
      amplitudeFieldErrors,
      gaForm,
      gaSaving,
      gaSuccess,
      gaError,
      gaFieldErrors,
      saveGoogleAnalytics,
      canManageAnalytics,
      saveAmplitude,
      providerStatusText,
      providerPillClass,
      // Recommendations (Action-to-Outcome Feedback Loop, BUI-F-16).
      recommendations,
      recRankingMode,
      recLearningActive,
      recLoading,
      recError,
      recActionPending,
      fetchRecommendations,
      markRecommendationDone,
    }
  }
}
</script>

<style scoped>
.ss-page {
  min-height: 100vh;
  color: var(--text-primary);
  background:
    radial-gradient(circle at 12% 0%, rgba(124,109,250,0.16), transparent 32rem),
    radial-gradient(circle at 86% 10%, rgba(56,189,248,0.10), transparent 28rem),
    var(--surface-base);
  padding: 28px 0 48px;
}
.ss-container {
  width: min(1280px, calc(100% - 32px));
  margin: 0 auto;
}

.ss-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}
.ss-header-actions { display: flex; gap: 10px; flex-shrink: 0; flex-wrap: wrap; }
.ss-kicker {
  margin: 0 0 6px;
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.ss-title {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(26px, 3.6vw, 36px);
  font-weight: 700;
  letter-spacing: -0.03em;
}
.ss-subtitle {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.ss-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  border: var(--glass-border);
  background: var(--glass-bg);
  color: var(--text-primary);
  text-decoration: none;
  white-space: nowrap;
}
.ss-btn:hover:not(:disabled) {
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
}
.ss-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ss-btn-ghost {
  background: var(--glass-bg);
  color: var(--text-secondary);
}
.ss-btn-ghost:hover:not(:disabled) {
  color: var(--text-primary);
}
.ss-btn-primary {
  background: var(--accent);
  border-color: rgba(124,109,250,0.5);
  color: #fff;
  box-shadow: 0 0 16px var(--accent-glow);
}
.ss-btn-primary:hover:not(:disabled) {
  background: #6d5ef0;
  box-shadow: 0 0 24px var(--accent-glow);
}
.ss-btn-danger {
  background: var(--danger);
  border-color: rgba(239,68,68,0.4);
  color: #fff;
}
.ss-btn-danger:hover:not(:disabled) {
  background: #ef4444;
}
.ss-btn-danger-outline {
  border-color: rgba(239,68,68,0.4);
  color: #fca5a5;
}
.ss-file-label {
  position: relative;
  cursor: pointer;
}

.ss-loading {
  text-align: center;
  padding: 64px 16px;
  color: var(--text-muted);
}
.ss-loading p { margin-top: 14px; font-size: 13px; }
.ss-spinner {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 3px solid rgba(255,255,255,0.10);
  border-top-color: var(--accent);
  margin: 0 auto;
  animation: ss-spin 1s linear infinite;
}
@keyframes ss-spin { to { transform: rotate(360deg); } }

.ss-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 18px;
}
@media (min-width: 1024px) {
  .ss-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ss-overview-band { grid-column: 1 / -1; }
}
.ss-overview-band { margin-bottom: 4px; }

.ss-card {
  background: var(--glass-bg);
  border: var(--glass-border);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border-radius: var(--radius-lg);
  box-shadow: var(--elev-sm);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.ss-card-dim { opacity: 0.65; }
.ss-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.ss-card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.ss-card-sub { margin: 2px 0 0; font-size: 12px; color: var(--text-secondary); }

/* Recommendations card (Action-to-Outcome Feedback Loop, BUI-F-16) */
.ss-rec-mode-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.ss-rec-mode-learned {
  color: var(--success);
  background: rgba(52, 211, 153, 0.12);
  border: 1px solid rgba(52, 211, 153, 0.35);
}
.ss-rec-mode-default {
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.ss-rec-list { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.ss-rec-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 13px;
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.ss-rec-item-main { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.ss-rec-title { font-size: 13px; font-weight: 600; color: var(--text-primary); }
.ss-rec-rationale {
  font-size: 11px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 46ch;
}
.ss-rec-done-btn {
  flex: none;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  background: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.35);
  cursor: pointer;
}
.ss-rec-done-btn:disabled { opacity: 0.6; cursor: default; }

.ss-status { display: flex; align-items: center; gap: 8px; }
.ss-status-text { font-size: 12px; color: var(--text-secondary); }
.ss-dot { width: 10px; height: 10px; border-radius: 999px; }
.ss-dot-success { background: var(--success); box-shadow: 0 0 8px rgba(52,211,153,0.45); }
.ss-dot-info    { background: #38bdf8; box-shadow: 0 0 8px rgba(56,189,248,0.45); }
.ss-dot-warning { background: var(--warning); box-shadow: 0 0 8px rgba(251,191,36,0.45); }
.ss-dot-danger  { background: var(--danger);  box-shadow: 0 0 8px rgba(239,68,68,0.45); }
.ss-dot-neutral { background: rgba(255,255,255,0.25); }

.ss-block { margin: 4px 0; }
.ss-stack { display: flex; flex-direction: column; gap: 12px; }

.ss-banner {
  padding: 11px 14px;
  border-radius: var(--radius-sm);
  font-size: 13px;
  border: 1px solid transparent;
}
.ss-banner-success { background: rgba(34,197,94,0.10);  border-color: rgba(34,197,94,0.3);  color: #86efac; }
.ss-banner-danger  { background: rgba(239,68,68,0.10);  border-color: rgba(239,68,68,0.3);  color: #fca5a5; }
.ss-banner-warning { background: rgba(245,158,11,0.10); border-color: rgba(245,158,11,0.3); color: #fde68a; }
.ss-banner-muted   { background: var(--glass-bg-active); border-color: rgba(255,255,255,0.08); color: var(--text-muted); }

.ss-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid transparent;
}
.ss-pill-success  { background: rgba(34,197,94,0.14);  color: #86efac; border-color: rgba(34,197,94,0.3); }
.ss-pill-danger   { background: rgba(239,68,68,0.14);  color: #fca5a5; border-color: rgba(239,68,68,0.3); }
.ss-pill-warning  { background: rgba(245,158,11,0.14); color: #fde68a; border-color: rgba(245,158,11,0.3); }
.ss-pill-info     { background: rgba(56,189,248,0.14); color: #93c5fd; border-color: rgba(56,189,248,0.3); }
.ss-pill-neutral  { background: var(--glass-bg-active); color: var(--text-muted); border-color: rgba(255,255,255,0.10); }

.ss-logo-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border-radius: var(--radius-sm);
  background: var(--glass-bg-active);
  border: var(--glass-border);
}
.ss-logo-thumb {
  width: 76px;
  height: 76px;
  border-radius: var(--radius-sm);
  background: rgba(255,255,255,0.06);
  border: var(--glass-border);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.ss-logo-img { max-width: 100%; max-height: 100%; object-fit: contain; }
.ss-logo-meta { min-width: 0; flex: 1; }
.ss-logo-name { margin: 0; font-size: 13px; font-weight: 600; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ss-logo-detail { margin: 3px 0 0; font-size: 11px; color: var(--text-muted); }
.ss-logo-renditions { margin: 6px 0 0; font-size: 10px; color: var(--text-muted); }

.ss-dropzone {
  border: 2px dashed rgba(255,255,255,0.16);
  border-radius: var(--radius-md);
  padding: 28px;
  text-align: center;
  background: var(--glass-bg-active);
}
.ss-dropzone-icon { width: 40px; height: 40px; color: var(--text-muted); margin: 0 auto 8px; }
.ss-dropzone-title { margin: 0; font-size: 13px; color: var(--text-secondary); }
.ss-dropzone-hint  { margin: 4px 0 0; font-size: 11px; color: var(--text-muted); }

.ss-actions-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

.ss-subcard {
  padding: 14px;
  border-radius: var(--radius-sm);
  background: var(--glass-bg-active);
  border: var(--glass-border);
}
.ss-subcard-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.ss-subcard-title { margin: 0; font-size: 13px; font-weight: 600; color: var(--text-primary); }

.ss-domain {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 16px;
  color: var(--accent);
}
.ss-domain-secondary {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 13px;
  color: var(--text-secondary);
}
.ss-domain-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 10px;
  font-size: 11px;
  color: var(--text-muted);
}
.ss-meta-label { color: var(--text-muted); font-weight: 600; }
.ss-meta-value { margin-left: 4px; }
.ss-meta-verified { color: #86efac; }
.ss-meta-pending  { color: #fde68a; }
.ss-meta-muted    { color: var(--text-muted); }

.ss-link-row { display: flex; align-items: center; gap: 8px; padding-top: 4px; }
.ss-link {
  background: none;
  border: 0;
  padding: 0;
  color: var(--accent);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
}
.ss-link:hover { color: var(--text-primary); }
.ss-link-sep { color: var(--text-muted); }

.ss-deploy-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}
.ss-chip-mono {
  display: inline-block;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(255,255,255,0.06);
  color: var(--text-primary);
  margin-left: 4px;
}
.ss-deploy-msg { font-size: 11px; color: var(--text-muted); }

/* Replaced deployment card: live / building / failed framing + history */
.ss-deploy-live { border-left: 2px solid var(--success); }
.ss-deploy-building { border-left: 2px solid var(--warning); }
.ss-deploy-failed { border-left: 2px solid var(--danger); }
.ss-pill-sm { padding: 1px 7px; font-size: 10px; }
.ss-deploy-note { font-size: 11px; color: #fca5a5; margin-top: 2px; }
.ss-deploy-history { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.ss-deploy-row { display: flex; align-items: center; gap: 8px; font-size: 12px; flex-wrap: wrap; }
.ss-deploy-env { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-secondary); min-width: 44px; }
.ss-deploy-commit { color: var(--text-secondary); }
.ss-deploy-time { margin-left: auto; font-size: 11px; color: var(--text-muted); }
.ss-deploy-checked { font-size: 10px; color: var(--text-muted); margin: 8px 0 0; text-align: right; }
.ss-empty-inline { font-size: 12px; color: var(--text-muted); }

.ss-empty-block {
  text-align: center;
  padding: 28px 16px;
  color: var(--text-muted);
}
.ss-empty-block p { margin: 6px 0 0; font-size: 13px; }
.ss-empty-icon { width: 44px; height: 44px; margin: 0 auto; color: var(--text-muted); }
.ss-empty-detail { margin-top: 4px !important; font-size: 11px; color: var(--text-muted); }

.ss-integration {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: var(--radius-sm);
  background: var(--glass-bg-active);
  border: var(--glass-border);
}
.ss-integration-dim { opacity: 0.65; }
.ss-integration-left { display: flex; align-items: center; gap: 10px; }
.ss-integration-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
}
.ss-integration-name { margin: 0; font-size: 13px; font-weight: 600; color: var(--text-primary); }
.ss-integration-desc { margin: 2px 0 0; font-size: 11px; color: var(--text-muted); }

.ss-error-state {
  text-align: center;
  padding: 56px 16px;
  color: var(--text-muted);
}
.ss-error-title { margin: 12px 0 4px; color: #fca5a5; font-size: 16px; font-weight: 700; }
.ss-error-detail { margin: 0 0 14px; font-size: 13px; color: var(--text-muted); }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

.ss-domain-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ss-domain-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
}
.ss-domain-row-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ss-domain-name {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.9);
  font-family: ui-monospace, monospace;
}
.ss-domain-remove {
  background: none;
  border: none;
  color: rgba(239,68,68,0.7);
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.ss-domain-remove:hover:not(:disabled) {
  color: #fca5a5;
  background: rgba(239,68,68,0.12);
}
.ss-domain-remove:disabled { opacity: 0.4; cursor: not-allowed; }
.ss-domain-remove-confirm {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px;
  background: rgba(239,68,68,0.15);
  color: #fca5a5;
  border-radius: 4px;
}
.ss-domain-row-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.ss-domain-confirm-text {
  font-size: 12px;
  color: rgba(255,255,255,0.55);
}
.ss-dns-copy {
  background: none;
  border: 1px solid rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.55);
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
  cursor: pointer;
  margin-left: auto;
  flex-shrink: 0;
  transition: color 0.15s, border-color 0.15s;
}
.ss-dns-copy:hover { color: rgba(255,255,255,0.9); border-color: rgba(255,255,255,0.35); }

.ss-add-domain { margin-top: 4px; }
.ss-domain-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ss-domain-input {
  padding: 0.65rem 0.9rem;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  font-size: 0.9rem;
  color: rgba(255,255,255,0.9);
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  width: 100%;
}
.ss-domain-input:focus {
  border-color: rgba(124,109,250,0.45);
  box-shadow: 0 0 0 3px rgba(124,109,250,0.15);
}
.ss-domain-input::placeholder { color: rgba(255,255,255,0.3); }
.ss-domain-form-actions { display: flex; gap: 8px; }
.ss-btn-sm { padding: 6px 14px; font-size: 12px; }
.ss-domain-error {
  font-size: 12px;
  color: #fca5a5;
  background: rgba(239,68,68,0.1);
  border: 1px solid rgba(239,68,68,0.25);
  border-radius: 8px;
  padding: 8px 12px;
}
.ss-domain-success {
  font-size: 12px;
  color: #86efac;
  background: rgba(34,197,94,0.1);
  border: 1px solid rgba(34,197,94,0.25);
  border-radius: 8px;
  padding: 8px 12px;
}

.ss-dns-instructions {
  background: rgba(245,158,11,0.08);
  border: 1px solid rgba(245,158,11,0.25);
  border-radius: 10px;
  padding: 14px 16px;
}
.ss-dns-title {
  font-size: 13px;
  font-weight: 600;
  color: #fde68a;
  margin: 0 0 6px;
}
.ss-dns-desc {
  font-size: 12px;
  color: rgba(255,255,255,0.6);
  margin: 0 0 10px;
}
.ss-dns-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}
.ss-dns-domain {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  font-family: ui-monospace, monospace;
}
.ss-dns-record {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0,0,0,0.25);
  border-radius: 6px;
  padding: 6px 10px;
}
.ss-dns-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(245,158,11,0.8);
  flex-shrink: 0;
}
.ss-dns-value {
  font-size: 12px;
  color: rgba(255,255,255,0.8);
  font-family: ui-monospace, monospace;
  word-break: break-all;
}
.ss-dns-note {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  margin: 8px 0 0;
}
.ss-seo-note {
  margin-bottom: 12px;
}
.ss-seo-guidance {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}
.ss-seo-guidance-heading {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255,255,255,0.5);
  margin-bottom: 8px;
}
.ss-seo-guidance-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ss-seo-guidance-item {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border-left: 3px solid transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.ss-seo-guidance-poor {
  background: rgba(248,113,113,0.08);
  border-left-color: rgba(248,113,113,0.6);
  color: rgba(255,200,200,0.9);
}
.ss-seo-guidance-warn {
  background: rgba(251,191,36,0.07);
  border-left-color: rgba(251,191,36,0.5);
  color: rgba(255,230,160,0.9);
}
.ss-seo-page-table {
  overflow-x: auto;
  display: block;
}
.ss-seo-td-url {
  max-width: 260px;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.ss-seo-page-action {
  font-size: 12px;
  color: rgba(124,109,250,0.9);
  text-decoration: none;
}
.ss-seo-page-action:hover {
  text-decoration: underline;
}
.ss-seo-retry-btn {
  margin-top: 8px;
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.07);
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  display: block;
}
.ss-seo-retry-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
/* ── SEO refresh button ───────────────────────────────────── */
.ss-seo-refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,0.75);
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}
.ss-seo-refresh-btn:hover:not(:disabled) { background: rgba(255,255,255,0.1); }
.ss-seo-refresh-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.ss-seo-refresh-icon { width: 13px; height: 13px; flex-shrink: 0; }
@keyframes ss-spin { to { transform: rotate(360deg); } }
.ss-spin { animation: ss-spin 0.9s linear infinite; }
.ss-seo-last-refresh {
  display: block;
  font-size: 11px;
  color: rgba(255,255,255,0.35);
  margin-top: 2px;
}
.ss-seo-commit {
  font-family: monospace;
  font-size: 10px;
  opacity: 0.7;
}
.ss-seo-guidance-msg { flex: 1; }
.ss-seo-fix-btn {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
  color: var(--accent, #6366f1);
  text-decoration: none;
  padding: 3px 8px;
  border: 1px solid rgba(99,102,241,0.35);
  border-radius: 5px;
  white-space: nowrap;
  transition: background 0.15s;
}
.ss-seo-fix-btn:hover { background: rgba(99,102,241,0.12); }

/* ── SEO progress bar ────────────────────────────────────── */
.ss-seo-progress-wrap {
  height: 3px;
  background: rgba(255,255,255,0.08);
  border-radius: 2px;
  margin: 8px 0 12px;
  overflow: hidden;
}
.ss-seo-progress-bar {
  height: 100%;
  background: var(--accent, #6366f1);
  border-radius: 2px;
  transition: width 0.4s ease;
}
.ss-seo-progress-fail { background: rgba(248,113,113,0.8); }

/* ── Horizontal SEO cards ────────────────────────────────── */
.ss-seo-cards { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }

.ss-seo-hcard {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 14px 16px;
}
.ss-seo-hcard-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  flex-shrink: 0;
}
.ss-seo-hcard-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  line-height: 1;
}
.ss-seo-hcard-value-sm { font-size: 22px; }
.ss-seo-hcard-sub {
  font-size: 12px;
  color: rgba(255,255,255,0.4);
  margin-top: 2px;
}
.ss-seo-hcard-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.ss-seo-hcard-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.55);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ss-seo-hcard-detail {
  font-size: 13px;
  color: rgba(255,255,255,0.7);
}

/* health tints */
.ss-seo-health-good { border-color: rgba(52,211,153,0.35); }
.ss-seo-health-warn { border-color: rgba(251,191,36,0.35); }
.ss-seo-health-poor { border-color: rgba(248,113,113,0.35); }
.ss-seo-health-good .ss-seo-hcard-value { color: rgba(52,211,153,0.95); }
.ss-seo-health-warn .ss-seo-hcard-value { color: rgba(251,191,36,0.95); }
.ss-seo-health-poor .ss-seo-hcard-value { color: rgba(248,113,113,0.95); }

/* ── Score breakdown bars ────────────────────────────────── */
.ss-seo-breakdown { display: flex; flex-direction: column; gap: 5px; }
.ss-seo-breakdown-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}
.ss-seo-breakdown-name {
  width: 110px;
  flex-shrink: 0;
  color: rgba(255,255,255,0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ss-seo-breakdown-bar-wrap {
  flex: 1;
  height: 5px;
  background: rgba(255,255,255,0.08);
  border-radius: 3px;
  overflow: hidden;
}
.ss-seo-breakdown-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.ss-seo-bar-full    { background: rgba(52,211,153,0.8); }
.ss-seo-bar-partial { background: rgba(251,191,36,0.8); }
.ss-seo-bar-zero    { background: rgba(248,113,113,0.6); }
.ss-seo-breakdown-pts {
  width: 36px;
  flex-shrink: 0;
  text-align: right;
  color: rgba(255,255,255,0.45);
  font-variant-numeric: tabular-nums;
}

/* ── Stats row (indexing / search analytics) ─────────────── */
.ss-seo-stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ss-seo-stat {
  flex: 1;
  min-width: 80px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ss-seo-stat-value {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}
.ss-seo-stat-label {
  font-size: 10px;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ss-seo-health-good .ss-seo-stat-value { color: rgba(52,211,153,0.9); }
.ss-seo-health-warn .ss-seo-stat-value { color: rgba(251,191,36,0.9); }
.ss-seo-health-poor .ss-seo-stat-value { color: rgba(248,113,113,0.9); }
.ss-seo-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.ss-seo-th {
  text-align: left;
  padding: 6px 8px;
  color: rgba(255,255,255,0.5);
  font-weight: 600;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.ss-seo-td {
  padding: 7px 8px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.8);
}
tr:last-child .ss-seo-td {
  border-bottom: none;
}
</style>
